import { Modal } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllSubtasks } from '../../api/v2/load';

const AddSubtaskModal = ({ show, onHide, onSubmit, isEpo = false, hasTraderUploadedIpaffs = false }) => {
  const [selectedSubtask, setSelectedSubtask] = useState('');
  const [adminFeeType, setAdminFeeType] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [subtaskOptions, setSubtaskOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { isPending, data, error } = useQuery({
    queryKey: ['getAllSubtasks'],
    queryFn: () => getAllSubtasks(),
    enabled: show,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (data?.length) {
      if (Array.isArray(data)) {
        const options = [];
        data.forEach((item) => {
          const isAdminFeeEntry =
            item.subTask != null && String(item.subTask).toLowerCase().replace(/\s+/g, ' ') === 'admin fee';
          if (isAdminFeeEntry) {
            // Parse the items JSON string for Admin fee (e.g. "ADMIN FEE" with nested items)
            const adminFeeItems = [];
            if (item.items) {
              try {
                const parsed = typeof item.items === 'string' ? JSON.parse(item.items) : item.items;
                adminFeeItems.push(...(Array.isArray(parsed) ? parsed : []));
              } catch (e) {
                console.error('Error parsing admin fee items:', e);
              }
            }
            // Set admin fee default value
            if (adminFeeItems.length > 0) {
              setAdminFeeType(adminFeeItems[0].subTask);
            }
            // Add Admin fee as an option
            options.push({
              subTasktId: item.subTasktId,
              value: item.subTask,
              label: item.subTask,
              isAdminFee: true,
              adminFeeItems: adminFeeItems,
            });
          } else {
            // Regular subtask
            options.push({
              subTasktId: item.subTasktId,
              value: item.subTask,
              label: item.subTask,
              isAdminFee: false,
            });
          }
        });
        setSubtaskOptions(options);
      }
    }
  }, [data]);

  const handleSubtaskChange = (value) => {
    setSelectedSubtask(value);
  };

  const handleSubmit = async () => {
    if (!selectedSubtask) return;

    const selectedOption = subtaskOptions.find((opt) => opt.value === selectedSubtask);

    // Check if CHED or IPAFFS is selected and trader has uploaded one (EPO only)
    const isChedIpaffs = selectedSubtask === 'CHED' || selectedSubtask === 'IPAFFS';

    if (isEpo && hasTraderUploadedIpaffs && isChedIpaffs) {
      setShowWarning(true);
      return;
    }

    const isAdminFee = selectedOption?.isAdminFee;

    // For Admin fee, find the selected admin fee item's subTasktId
    let subTasktId = selectedOption?.subTasktId;
    if (isAdminFee && adminFeeType && selectedOption?.adminFeeItems) {
      const selectedFeeItem = selectedOption.adminFeeItems.find((fee) => fee.subTask === adminFeeType);
      if (selectedFeeItem) {
        subTasktId = selectedFeeItem.subTasktId;
      }
    }

    const subtaskData = {
      subTasktId: subTasktId,
      type: selectedSubtask,
      ...(isAdminFee && adminFeeType && { adminFeeType }),
    };

    setSubmitting(true);
    try {
      await onSubmit(subtaskData);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedSubtask('');
    // Reset admin fee type when closing
    const adminFeeOption = subtaskOptions.find((opt) => opt.isAdminFee);
    if (adminFeeOption?.adminFeeItems?.length > 0) {
      setAdminFeeType(adminFeeOption.adminFeeItems[0].subTask);
    }
    setShowWarning(false);
    onHide();
  };

  const handleWarningConfirm = async () => {
    setShowWarning(false);
    const selectedOption = subtaskOptions.find((opt) => opt.value === selectedSubtask);
    const isAdminFee = selectedOption?.isAdminFee;

    // For Admin fee, find the selected admin fee item's subTasktId
    let subTasktId = selectedOption?.subTasktId;
    if (isAdminFee && adminFeeType && selectedOption?.adminFeeItems) {
      const selectedFeeItem = selectedOption.adminFeeItems.find((fee) => fee.subTask === adminFeeType);
      if (selectedFeeItem) {
        subTasktId = selectedFeeItem.subTasktId;
      }
    }

    const subtaskData = {
      subTasktId: subTasktId,
      type: selectedSubtask,
      ...(isAdminFee && adminFeeType && { adminFeeType }),
    };
    setSubmitting(true);
    try {
      await onSubmit(subtaskData);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const getErrorMessage = () => {
    if (!error) return null;

    try {
      const errorResponse = JSON.parse(error.message);
      if (typeof errorResponse === 'string') return errorResponse;
      return errorResponse?.message || errorResponse?.error || 'Failed to load subtask options.';
    } catch {
      return error.message || 'Failed to load subtask options.';
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="md" animation={false}>
        <Modal.Header>
          <Modal.Title style={{ fontSize: '1.4rem' }}>Add Subtask</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          {isPending ? (
            <div className="text-center py-4">Loading subtask options...</div>
          ) : error ? (
            <div className="alert alert-warning">{getErrorMessage() || 'Failed to load subtask options.'}</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {subtaskOptions.map((option) => (
                <div key={option.value} className="d-flex gap-3">
                  <input
                    type="radio"
                    name="subtask"
                    id={option.value}
                    value={option.value}
                    checked={selectedSubtask === option.value}
                    onChange={(e) => handleSubtaskChange(e.target.value)}
                    style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer', marginTop: '0.1rem' }}
                  />
                  <label htmlFor={option.value} style={{ cursor: 'pointer', flex: 1 }}>
                    {option.label}
                    {option.label === 'IPAFFS' && selectedSubtask === option.value && (
                      <div className="text-eorired" style={{ fontSize: 12 }}>
                        To avoid duplication, please ensure you are not adding an IPAFFS reference submitted by the
                        trader.
                      </div>
                    )}
                  </label>

                  {option.isAdminFee &&
                    selectedSubtask === option.value &&
                    option.adminFeeItems &&
                    option.adminFeeItems.length > 0 && (
                      <select
                        className="form-select form-select-sm"
                        value={adminFeeType}
                        onChange={(e) => setAdminFeeType(e.target.value)}
                        style={{ width: 'auto', minWidth: '200px' }}
                      >
                        {option.adminFeeItems.map((feeItem) => (
                          <option key={feeItem.subTasktId} value={feeItem.subTask}>
                            {feeItem.subTask}
                          </option>
                        ))}
                      </select>
                    )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          className="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-between align-items-center"
          style={{
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
          }}
        >
          <button className="cancel-button" onClick={handleClose} style={{ borderRadius: '10px' }}>
            Cancel
          </button>
          <button
            className="blue-button-teams"
            onClick={handleSubmit}
            disabled={!selectedSubtask || submitting}
            style={{ borderRadius: '10px' }}
          >
            {submitting ? 'Adding...' : 'Submit'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Warning Modal for EPO when trader has uploaded IPAFFS */}
      {showWarning && (
        <Modal show={showWarning} onHide={() => setShowWarning(false)} centered size="md" animation={false}>
          <Modal.Header>
            <Modal.Title className="d-flex gap-3 align-items-center py-2">
              <div className="warning" />
              <span style={{ fontSize: '1.4rem' }}>A Trader has already uploaded an IPAFFS.</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-4">
            <p>To avoid duplication, please confirm you are not adding an IPAFFS reference submitted by the trader.</p>
          </Modal.Body>
          <Modal.Footer
            className="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-between align-items-center"
            style={{
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
            }}
          >
            <button className="cancel-button" onClick={() => setShowWarning(false)} style={{ borderRadius: '10px' }}>
              Cancel
            </button>
            <button className="blue-button-teams" onClick={handleWarningConfirm} style={{ borderRadius: '10px' }}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default AddSubtaskModal;
